import {
  Button as AntButton,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Switch,
} from 'antd';
import styles from './createFxExchangeRate.module.scss';
import {currencySymbols, walletCurrencies} from 'utils/mock';
import {useNavigate, useParams} from 'react-router-dom';
import {ArrowLeftOutlined} from '@ant-design/icons';
// The card vocabulary from the wallet currency form, which is this screen's
// sibling — same kind of settings page, so the same shape.
import formShell from '../../create/createWalletCurrency.module.scss';
import trashIcon from 'images/trash-can-outline.svg';

import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {
  CREATE_FX_EXCHANGE_RATE,
  UPDATE_FX_EXCHANGE_RATE,
} from 'graphql/mutations/wallet';
import {toast} from 'react-toastify';
import {useEffect, useState} from 'react';
import {
  GET_ALL_FX_EXCHANGE_RATES,
  GET_FX_EXCHANGE_RATE_BY_ID,
  GET_PUBLIC_EXCHANGE_RATES,
} from 'graphql/queries/wallet';
import moment from 'moment';
import DBModal from 'components/modal/modal';
import {Button} from '@dabafinance/react-components';
import {fieldRules} from 'utils/helper';

const CreateFXExchangeRate = () => {
  const [sourceCurrency, setSourceCurrency] = useState('');
  const [targetCurrency, setTargetCurrency] = useState('');
  const [currentRate, setCurrentRate] = useState({});
  const [finishModal, setFinishModal] = useState(false);
  const [fieldValues, setFieldValues] = useState({});
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const {id} = useParams();
  const feeSchedule = Form.useWatch('feeSchedule', form);
  const discountFeeSchedule = Form.useWatch('discountFeeSchedule', form);
  const sourceCurrencySymbol = sourceCurrency
    ? currencySymbols[sourceCurrency]?.symbol
    : null;
  const targetCurrencySymbol = targetCurrency
    ? currencySymbols[targetCurrency]?.symbol
    : null;

  const {data, loading} = useQuery(GET_FX_EXCHANGE_RATE_BY_ID, {
    variables: {
      getFxExchangeRateByIdId: id,
    },
    onError: error => toast.error(error.message),
    skip: !id,
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  const [createFxExchangeRate, {loading: loadingCreate}] = useMutation(
    CREATE_FX_EXCHANGE_RATE,
  );

  const [updateFxExchangeRate, {loading: loadingUpdate}] = useMutation(
    UPDATE_FX_EXCHANGE_RATE,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [getPublicFxExchangeRates, {loading: loadingRates}] = useLazyQuery(
    GET_PUBLIC_EXCHANGE_RATES,
  );

  const getUsedFeeTypes = (currentField, fields, formWatch) => {
    const usedTypes = [];
    fields.forEach(field => {
      const fieldValue = formWatch?.[field.name]?.feeType;
      if (field.key !== currentField && fieldValue) {
        usedTypes.push(fieldValue);
      }
    });
    return usedTypes.includes(currentField);
  };

  useEffect(() => {
    if (id && data) {
      const dbData = data?.getFxExchangeRateById;
      const normalizedTieredSchedule = (
        dbData?.tieredDiscountSchedule || []
      ).map(item =>
        item?.applyTo === 'RATE'
          ? {
              ...item,
              discountType: 'FIXED',
              variableFeeAmount: undefined,
            }
          : item,
      );
      setSourceCurrency(dbData?.sourceCurrency);
      setTargetCurrency(dbData?.targetCurrency);
      form.setFieldsValue({
        sourceCurrency: dbData?.sourceCurrency,
        targetCurrency: dbData?.targetCurrency,
        rate: dbData?.rate,
        minAmount: dbData?.minSwapAmountInSourceCurrency,
        feeSchedule: dbData?.feeSchedule,
        discountFeeSchedule: dbData?.discount,
        requireDepositForTargetCurrencySwap:
          dbData?.requireDepositForTargetCurrencySwap ?? false,
        requirePriorSwapFromTargetCurrency:
          dbData?.requirePriorSwapFromTargetCurrency ?? false,
        maxAmount: dbData?.maxSwapAmountInSourceCurrency,
        feeType: dbData?.feeSchedule?.mode,
        variableFee: dbData?.feeSchedule?.variableFee,
        fixedFee: dbData?.feeSchedule?.fixedFee,
        shouldCheckMarketRate: dbData?.shouldCheckMarketRate,
        tieredDiscountSchedule: normalizedTieredSchedule,
      });
    }
  }, [data]);

  useEffect(() => {
    if (sourceCurrency && targetCurrency) {
      getPublicFxExchangeRates({
        variables: {
          from: sourceCurrency,
          to: [targetCurrency],
        },
        onCompleted: data => {
          setCurrentRate(data?.getPublicFxExchangeRates);
        },
        fetchPolicy: 'no-cache',
      });
    }
  }, [sourceCurrency, targetCurrency]);

  const handleSubmit = () => {
    const values = fieldValues;
    const mapFeeScheduleItem = data => {
      const base = {feeType: data?.feeType};
      if (data?.feeType === 'FIXED') {
        const val =
          data?.fixedFeeAmount !== undefined && data?.fixedFeeAmount !== ''
            ? Number(data?.fixedFeeAmount)
            : undefined;
        if (val !== undefined) base.fixedFeeAmount = val;
      } else {
        const val =
          data?.variableFeeAmount !== undefined &&
          data?.variableFeeAmount !== ''
            ? Number(data?.variableFeeAmount)
            : undefined;
        if (val !== undefined) base.variableFeeAmount = val;
      }
      return base;
    };
    const mappedFeeSchedule = (values?.feeSchedule || []).map(
      mapFeeScheduleItem,
    );
    const mappedDiscountSchedule = (values?.discountFeeSchedule || []).map(
      mapFeeScheduleItem,
    );
    const mappedTieredDiscountSchedule = (
      values?.tieredDiscountSchedule || []
    ).map(data => {
      const base = {
        applyTo: data?.applyTo,
        discountType: data?.applyTo === 'RATE' ? 'FIXED' : data?.discountType,
        minimumAmountThreshold: Number(data?.minimumAmountThreshold),
      };
      const isFixed =
        data?.discountType === 'FIXED' || data?.applyTo === 'RATE';
      if (isFixed) {
        const val =
          data?.fixedFeeAmount !== undefined && data?.fixedFeeAmount !== ''
            ? Number(data?.fixedFeeAmount)
            : undefined;
        if (val !== undefined) base.fixedFeeAmount = val;
      } else {
        const val =
          data?.variableFeeAmount !== undefined &&
          data?.variableFeeAmount !== ''
            ? Number(data?.variableFeeAmount)
            : undefined;
        if (val !== undefined) base.variableFeeAmount = val;
      }
      return base;
    });

    const optionalFields = {
      input: {
        sourceCurrency: values.sourceCurrency,
        targetCurrency: values.targetCurrency,
        rate: Number(values.rate),
        shouldCheckMarketRate: values?.shouldCheckMarketRate,
        // Always sent, never left undefined. `ExchangeRateInput` declares
        // this one as `Boolean = false`, and GraphQL applies an input default
        // when the field is absent — so an update that omitted it would
        // silently switch the gate off on a pair that had it on.
        requireDepositForTargetCurrencySwap:
          values?.requireDepositForTargetCurrencySwap ?? false,
        requirePriorSwapFromTargetCurrency:
          values?.requirePriorSwapFromTargetCurrency ?? false,
        ...(values.maxAmount && {
          maxSwapAmountInSourceCurrency: Number(values?.maxAmount),
        }),
        ...(values.minAmount && {
          minSwapAmountInSourceCurrency: Number(values?.minAmount),
        }),
        feeSchedule: mappedFeeSchedule,
        ...(values?.discountFeeSchedule && {
          discount: mappedDiscountSchedule,
        }),
        ...(values?.tieredDiscountSchedule && {
          tieredDiscountSchedule: mappedTieredDiscountSchedule,
        }),
      },
    };
    if (id) {
      updateFxExchangeRate({
        variables: {
          ...optionalFields,
        },
        refetchQueries: [{query: GET_ALL_FX_EXCHANGE_RATES}],
      })
        .then(({data: {updateFxExchangeRate}}) => {
          if (updateFxExchangeRate.__typename === 'Error') {
            toast.error(updateFxExchangeRate.message);
          } else {
            toast.success('Swap Pair Update Successfully');
            navigate(-1);
          }
        })
        .catch(error => {
          toast.error(error);
        });
    } else
      createFxExchangeRate({
        variables: {
          ...optionalFields,
        },
        refetchQueries: [{query: GET_ALL_FX_EXCHANGE_RATES}],
      })
        .then(({data: {createFxExchangeRate}}) => {
          if (createFxExchangeRate.__typename === 'Error') {
            toast.error(createFxExchangeRate.message);
          } else {
            toast.success('Swap Pair Created Successfully');
            navigate(-1);
          }
        })
        .catch(error => {
          toast.error(error);
        });
  };

  if (id && loading) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <div className={formShell.page}>
      <div className={formShell.header}>
        <div className={formShell.titleWrap}>
          <button
            type="button"
            className={formShell.backButton}
            onClick={() => navigate('/dashboard/fx-exchange-rate')}>
            <ArrowLeftOutlined /> Swap pairs
          </button>
          <h1 className={formShell.title}>
            {id
              ? `${sourceCurrency || ''} → ${targetCurrency || ''} swap pair`
              : 'New swap pair'}
          </h1>
          <p className={formShell.subtitle}>
            {id
              ? 'Change the rate, limits and restrictions on this direction. The reverse direction is a separate pair.'
              : 'Set up one direction of a currency swap. You will need a second pair for the reverse direction.'}
          </p>
        </div>
      </div>
      <div className={styles.background}>
        <Form
          form={form}
          onFinish={e => {
            setFieldValues(e);
            setFinishModal(true);
          }}
          layout="vertical">
          <section className={formShell.card}>
            <div className={formShell.cardHeader}>
              <h2 className={formShell.cardTitle}>Pair</h2>
              <p className={formShell.cardHint}>
                The direction money moves. Each direction is its own pair.
              </p>
            </div>
            <Form.Item
              rules={[{required: true, message: 'This Field is required'}]}
              name="sourceCurrency"
              className={styles.bigWidth}
              label="Source Currency">
              <Select value={sourceCurrency} onChange={setSourceCurrency}>
                {walletCurrencies.map(data => (
                  <Select.Option key={data.key}>{data.key}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              className={styles.bigWidth}
              rules={[{required: true, message: 'This Field is required'}]}
              name="targetCurrency"
              label="Target Currency">
              <Select value={targetCurrency} onChange={setTargetCurrency}>
                {walletCurrencies.map(data => (
                  <Select.Option key={data.key}>{data.key}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </section>

          <section className={formShell.card}>
            <div className={formShell.cardHeader}>
              <h2 className={formShell.cardTitle}>Limits</h2>
              <p className={formShell.cardHint}>
                The smallest and largest amount a user may swap in one go, in
                the source currency.
              </p>
            </div>
            <Form.Item
              name="minAmount"
              tooltip="If user enters less than this source-currency amount, swap is not allowed."
              label="Minimum source-currency amount allowed for a swap">
              <InputNumber
                className={styles.bigWidth}
                formatter={value =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
              />
            </Form.Item>
            <Form.Item
              name="maxAmount"
              tooltip="If user enters more than this source-currency amount, swap is not allowed."
              label="Maximum source-currency amount allowed for a swap">
              <InputNumber
                className={styles.bigWidth}
                formatter={value =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
              />
            </Form.Item>
          </section>

          <section className={formShell.card}>
            <div className={formShell.cardHeader}>
              <h2 className={formShell.cardTitle}>Rate</h2>
              <p className={formShell.cardHint}>
                What one unit of the source currency is worth, and whether to
                sanity-check it against the market.
              </p>
            </div>
            <div className={styles.rate}>
              <Form.Item
                rules={[{required: true, message: 'This Field is required'}]}
                name="rate"
                className={styles.bigWidth}
                tooltip="Base exchange rate before extra rules. Example: if 1 USD = 650 XOF, enter 650."
                label="Base exchange rate (target currency per 1 source currency)">
                <Input type="number" />
              </Form.Item>
              <div className={styles.values}>
                {loadingRates ? (
                  <Spin />
                ) : Object.values(currentRate).length ? (
                  <p>
                    1 {sourceCurrency} = {currentRate?.to[0]?.rate}
                    {targetCurrency}{' '}
                    <span>
                      (
                      {moment(currentRate?.lastUpdated).format(
                        'DD MMMM YYYY hh:mm A',
                      )}
                      )
                    </span>
                  </p>
                ) : (
                  ''
                )}
              </div>
            </div>
            <Form.Item
              name="shouldCheckMarketRate"
              tooltip="When enabled, the system checks current market rate and can use it as a safeguard."
              label="Use market rate safety check">
              <Switch
                defaultChecked={
                  data?.getFxExchangeRateById?.shouldCheckMarketRate
                }
              />
            </Form.Item>
          </section>

          <section className={formShell.card}>
            <div className={formShell.cardHeader}>
              <h2 className={formShell.cardTitle}>Who may use this pair</h2>
              <p className={formShell.cardHint}>
                Leave both off to open the pair to everyone. When more than one
                is on, all of them must pass.
              </p>
            </div>
            {/* Both gates are per direction: this is the pair as configured,
              not the pair reversed. All gates set on a pair must pass. */}
            <Form.Item
              name="requireDepositForTargetCurrencySwap"
              valuePropName="checked"
              tooltip={`The user must already have a successful deposit in ${
                targetCurrency || 'the target currency'
              } to use this pair. Stops people swapping into a currency they never funded.`}
              label="Require a deposit in the target currency">
              <Switch />
            </Form.Item>
            <Form.Item
              name="requirePriorSwapFromTargetCurrency"
              valuePropName="checked"
              tooltip={`The user must already have a successful ${
                targetCurrency || 'target'
              } → ${
                sourceCurrency || 'source'
              } swap. An exit path for a currency with no withdrawal support: only people who swapped in this way can swap back out.`}
              label="Require a prior swap from the target currency">
              <Switch />
            </Form.Item>
          </section>

          <section className={formShell.card}>
            <div className={formShell.cardHeader}>
              <h2 className={formShell.cardTitle}>Fees and discounts</h2>
              <p className={formShell.cardHint}>
                What the platform charges on this pair, what Daba Pro members
                pay instead, and any tiered adjustment by amount.
              </p>
            </div>
            <div className={styles.feeScheduleWrapper}>
              <Form.List className={styles.feeSchedule} name="feeSchedule">
                {(fields, {add, remove}) => (
                  <>
                    {fields.map(({name}) => (
                      <div className={styles.fees} key={name}>
                        <Form.Item
                          rules={[
                            {required: true, message: 'This field is required'},
                          ]}
                          name={[name, 'feeType']}
                          className={styles.feeField}
                          tooltip="Choose how the base fee is calculated for this swap pair."
                          label="How is the standard fee calculated?">
                          <Select>
                            <Select.Option
                              disabled={getUsedFeeTypes(
                                'FIXED',
                                fields,
                                feeSchedule,
                              )}
                              key="FIXED">
                              Fixed
                            </Select.Option>
                            <Select.Option
                              disabled={getUsedFeeTypes(
                                'VARIABLE',
                                fields,
                                feeSchedule,
                              )}
                              key="VARIABLE">
                              Variable
                            </Select.Option>
                          </Select>
                        </Form.Item>
                        {feeSchedule?.[name]?.feeType === 'VARIABLE' ? (
                          <Form.Item
                            rules={[fieldRules('This')]}
                            name={[name, 'variableFeeAmount']}
                            className={styles.feeField}
                            tooltip="Percentage of swap amount charged as fee."
                            label="Standard fee percentage (%)">
                            <Input type="number" />
                          </Form.Item>
                        ) : (
                          <Form.Item
                            rules={[fieldRules('This')]}
                            name={[name, 'fixedFeeAmount']}
                            className={styles.feeField}
                            tooltip="Flat amount charged as fee, regardless of amount size."
                            label="Standard fixed fee amount">
                            <Input type="number" />
                          </Form.Item>
                        )}
                        <button
                          type="button"
                          className={styles.removeFeeButton}
                          onClick={() => remove(name)}>
                          <img src={trashIcon} alt="remove" />
                        </button>
                      </div>
                    ))}
                    <div role="button" onClick={add} className={styles['add']}>
                      <h2>Add Fee</h2>
                    </div>
                  </>
                )}
              </Form.List>

              <h2>Discount Schedule (Applied to Daba Pro Users)</h2>
              <Form.List
                className={styles.feeSchedule}
                name="discountFeeSchedule">
                {(fields, {add, remove}) => (
                  <>
                    {fields.map(({name}) => (
                      <div className={styles.fees} key={name}>
                        <Form.Item
                          rules={[
                            {required: true, message: 'This field is required'},
                          ]}
                          name={[name, 'feeType']}
                          className={styles.feeField}
                          tooltip="Choose how Daba Pro discount should reduce the standard fee."
                          label="How should Daba Pro discount be applied?">
                          <Select>
                            <Select.Option
                              disabled={getUsedFeeTypes(
                                'FIXED',
                                fields,
                                discountFeeSchedule,
                              )}
                              key="FIXED">
                              Fixed Discount
                            </Select.Option>
                            <Select.Option
                              disabled={getUsedFeeTypes(
                                'VARIABLE',
                                fields,
                                discountFeeSchedule,
                              )}
                              key="VARIABLE">
                              Variable Discount
                            </Select.Option>
                          </Select>
                        </Form.Item>
                        {discountFeeSchedule?.[name]?.feeType === 'VARIABLE' ? (
                          <Form.Item
                            rules={[fieldRules('This')]}
                            name={[name, 'variableFeeAmount']}
                            className={styles.feeField}
                            tooltip="Percentage discount applied to the variable fee."
                            label="Daba Pro variable discount (%)">
                            <Input type="number" />
                          </Form.Item>
                        ) : (
                          <Form.Item
                            rules={[fieldRules('This')]}
                            name={[name, 'fixedFeeAmount']}
                            className={styles.feeField}
                            tooltip="Flat discount amount applied to the fee."
                            label="Daba Pro fixed discount amount">
                            <Input type="number" />
                          </Form.Item>
                        )}
                        <button
                          type="button"
                          className={styles.removeFeeButton}
                          onClick={() => remove(name)}>
                          <img src={trashIcon} alt="remove" />
                        </button>
                      </div>
                    ))}
                    <div role="button" onClick={add} className={styles['add']}>
                      <h2>Add Discount</h2>
                    </div>
                  </>
                )}
              </Form.List>
            </div>
            <div className={styles.tieredScheduleWrapper}>
              <h2>Tiered Adjustment Schedule</h2>
              <p className={styles.tieredDiscountHint}>
                Each row applies when user swap amount is greater than or equal
                to the threshold, measured in source currency.
              </p>
              <Form.List
                className={styles.feeSchedule}
                name="tieredDiscountSchedule">
                {(fields, {add, remove}) => (
                  <>
                    {fields.map(({name}) => (
                      <div className={styles.tieredFees} key={name}>
                        <Form.Item
                          rules={[
                            {required: true, message: 'This field is required'},
                          ]}
                          name={[name, 'applyTo']}
                          className={styles.tieredField}
                          tooltip="Choose whether this tier changes the fee amount or adjusts the exchange rate."
                          label="What should this tier adjust?">
                          <Select
                            onChange={value => {
                              if (value === 'RATE') {
                                form.setFieldValue(
                                  [
                                    'tieredDiscountSchedule',
                                    name,
                                    'discountType',
                                  ],
                                  'FIXED',
                                );
                                form.setFieldValue(
                                  [
                                    'tieredDiscountSchedule',
                                    name,
                                    'variableFeeAmount',
                                  ],
                                  undefined,
                                );
                              }
                            }}>
                            <Select.Option key="FEE">Fee</Select.Option>
                            <Select.Option key="RATE">Rate</Select.Option>
                          </Select>
                        </Form.Item>
                        <Form.Item
                          rules={[
                            {required: true, message: 'This field is required'},
                          ]}
                          name={[name, 'minimumAmountThreshold']}
                          tooltip="When swap amount in source currency reaches this value, this tier rule starts applying."
                          className={styles.tieredField}
                          label="Minimum source-currency amount to activate this tier">
                          <Input type="number" />
                        </Form.Item>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues?.tieredDiscountSchedule?.[name]
                              ?.applyTo !==
                            currentValues?.tieredDiscountSchedule?.[name]
                              ?.applyTo
                          }>
                          {() => {
                            const applyTo = form.getFieldValue([
                              'tieredDiscountSchedule',
                              name,
                              'applyTo',
                            ]);
                            if (applyTo === 'RATE') return null;
                            return (
                              <Form.Item
                                rules={[
                                  {
                                    required: true,
                                    message: 'This field is required',
                                  },
                                ]}
                                name={[name, 'discountType']}
                                className={styles.tieredField}
                                tooltip="For Fee adjustment: choose fixed or percentage."
                                label="How should this tier value be calculated?">
                                <Select
                                  onChange={value => {
                                    if (value === 'FIXED') {
                                      form.setFieldValue(
                                        [
                                          'tieredDiscountSchedule',
                                          name,
                                          'variableFeeAmount',
                                        ],
                                        undefined,
                                      );
                                    } else if (value === 'VARIABLE') {
                                      form.setFieldValue(
                                        [
                                          'tieredDiscountSchedule',
                                          name,
                                          'fixedFeeAmount',
                                        ],
                                        undefined,
                                      );
                                    }
                                  }}>
                                  <Select.Option key="FIXED">
                                    Fixed
                                  </Select.Option>
                                  <Select.Option key="VARIABLE">
                                    Variable
                                  </Select.Option>
                                </Select>
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues?.tieredDiscountSchedule?.[name]
                              ?.applyTo !==
                              currentValues?.tieredDiscountSchedule?.[name]
                                ?.applyTo ||
                            prevValues?.tieredDiscountSchedule?.[name]
                              ?.discountType !==
                              currentValues?.tieredDiscountSchedule?.[name]
                                ?.discountType
                          }>
                          {() => {
                            const applyTo =
                              form.getFieldValue([
                                'tieredDiscountSchedule',
                                name,
                                'applyTo',
                              ]) || 'FEE';
                            const discountType = form.getFieldValue([
                              'tieredDiscountSchedule',
                              name,
                              'discountType',
                            ]);
                            const effectiveDiscountType =
                              applyTo === 'RATE' ? 'FIXED' : discountType;
                            const showFixedField =
                              effectiveDiscountType === 'FIXED';
                            const showVariableField =
                              effectiveDiscountType === 'VARIABLE';
                            const fixedAmountSymbol =
                              applyTo === 'RATE'
                                ? targetCurrencySymbol
                                : sourceCurrencySymbol;

                            return (
                              <>
                                {showFixedField && (
                                  <Form.Item
                                    rules={[fieldRules('This')]}
                                    name={[name, 'fixedFeeAmount']}
                                    className={styles.tieredField}
                                    tooltip={
                                      applyTo === 'RATE'
                                        ? 'Value added to exchange rate when this tier is active.'
                                        : 'Flat fee adjustment used when this tier is active.'
                                    }
                                    label={
                                      applyTo === 'RATE'
                                        ? 'Rate adjustment value to add'
                                        : 'Fixed fee adjustment value'
                                    }>
                                    <Input
                                      type="number"
                                      addonBefore={
                                        fixedAmountSymbol || undefined
                                      }
                                    />
                                  </Form.Item>
                                )}
                                {showVariableField && (
                                  <Form.Item
                                    rules={[fieldRules('This')]}
                                    name={[name, 'variableFeeAmount']}
                                    className={styles.tieredField}
                                    tooltip="Percentage-based fee adjustment for this tier."
                                    label="Percentage fee adjustment (%)">
                                    <Input type="number" />
                                  </Form.Item>
                                )}
                              </>
                            );
                          }}
                        </Form.Item>
                        <button
                          type="button"
                          className={styles.removeTierButton}
                          onClick={() => remove(name)}>
                          <img src={trashIcon} alt="remove tier" />
                        </button>
                      </div>
                    ))}
                    <div role="button" onClick={add} className={styles['add']}>
                      <h2>Add Tier</h2>
                    </div>
                  </>
                )}
              </Form.List>
            </div>
          </section>

          <DBModal
            isOpen={finishModal}
            handleClose={() => setFinishModal(false)}
            content={
              <div className="finishScreen">
                <h1>
                  You are about to {id ? 'Update' : 'Create'} A Currency Pair
                  for Swap
                </h1>
                <p>Click Yes to Confirm and Proceed</p>
                <div className="finish-buttons">
                  <Button
                    className="finish-button"
                    label="NO"
                    type="secondary"
                    onClick={() => setFinishModal(false)}
                  />
                  <Button
                    className="finish-button"
                    disabled={loadingCreate || loadingUpdate}
                    label="YES"
                    onClick={handleSubmit}
                  />
                </div>
              </div>
            }
          />
          <div className={formShell.footer}>
            <AntButton onClick={() => navigate('/dashboard/fx-exchange-rate')}>
              Cancel
            </AntButton>
            <AntButton
              type="primary"
              htmlType="submit"
              className={formShell.primaryButton}>
              {id ? 'Review changes' : 'Review and create'}
            </AntButton>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default CreateFXExchangeRate;
